import { useState, useMemo } from 'react';
import { Icon } from '@iconify/react';
import searchFill from '@iconify/icons-eva/search-fill';
import roundFilterList from '@iconify/icons-ic/round-filter-list';
import FilterListOffIcon from '@mui/icons-material/FilterListOff';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import GridOnIcon from '@mui/icons-material/GridOn';
import ReceiptIcon from '@mui/icons-material/Receipt';
import CallReceivedIcon from '@mui/icons-material/CallReceived';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DownloadIcon from '@mui/icons-material/Download';
import CloseIcon from '@mui/icons-material/Close';
import { MdOutlineSystemUpdateAlt } from 'react-icons/md';
import { orderBy as lodashOrderBy } from 'lodash';

import {
  Box,
  Button,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Tooltip,
  Typography
} from '@mui/material';
import Scrollbar from '../../../components/Scrollbar';
import SearchNotFound from '../../../components/SearchNotFound';
import SimplePagination from '../../../components/SimplePagination';
import { UserListHead } from '../../../components/_dashboard/user';
import Label from '../../../components/Label';
import { RootStyle, SearchStyle, StyledTableRow, StyledTableCell } from '../../Global/Styling';

// ── Table columns ─────────────────────────────────────────────────────
const TABLE_HEAD = [
  { id: 'invoiceDate', label: 'Inv. Date' },
  { id: 'invoiceNo', label: 'Invoice No.' },
  { id: 'supplierName', label: 'Supplier Name' },
  { id: 'gstin', label: 'GSTIN' },
  { id: 'taxableVal', label: 'Taxable Val.' },
  { id: 'cgst', label: 'CGST' },
  { id: 'sgst', label: 'SGST' },
  { id: 'igst', label: 'IGST' },
  { id: 'totalGst', label: 'Total GST' },
  { id: 'totalInv', label: 'Total Inv.' },
  { id: 'itcStatus', label: 'ITC Status' }
];

// ── Mock Purchase Data (replace with API) ─────────────────────────────
const MOCK_PURCHASES = [
  {
    id: 1,
    invoiceDate: '2024-04-03',
    invoiceNo: 'PURCH/24-25/001',
    supplierName: 'Reliance Industries Ltd',
    gstin: '27AAARL0001A1Z5',
    taxableVal: 85000,
    cgst: 7650,
    sgst: 7650,
    igst: 0,
    totalGst: 15300,
    totalInv: 100300,
    itcStatus: 'Eligible',
    type: 'B2B'
  },
  {
    id: 2,
    invoiceDate: '2024-04-12',
    invoiceNo: 'PURCH/24-25/002',
    supplierName: 'TechSupply Pvt Ltd',
    gstin: '09BBBTS2222B2Z6',
    taxableVal: 150000,
    cgst: 0,
    sgst: 0,
    igst: 27000,
    totalGst: 27000,
    totalInv: 177000,
    itcStatus: 'Eligible',
    type: 'B2B'
  },
  {
    id: 3,
    invoiceDate: '2024-05-08',
    invoiceNo: 'PURCH/24-25/003',
    supplierName: 'Office Mart',
    gstin: '33CCCOF3333C3Z7',
    taxableVal: 12000,
    cgst: 900,
    sgst: 900,
    igst: 0,
    totalGst: 1800,
    totalInv: 13800,
    itcStatus: 'Ineligible',
    type: 'B2C'
  },
  {
    id: 4,
    invoiceDate: '2024-05-20',
    invoiceNo: 'PURCH/24-25/004',
    supplierName: 'Global Freight Co',
    gstin: '06DDDGF4444D4Z8',
    taxableVal: 45000,
    cgst: 4050,
    sgst: 4050,
    igst: 0,
    totalGst: 8100,
    totalInv: 53100,
    itcStatus: 'Eligible',
    type: 'B2B'
  },
  {
    id: 5,
    invoiceDate: '2024-06-05',
    invoiceNo: 'PURCH/24-25/005',
    supplierName: 'Digital Services Ltd',
    gstin: '07EEEEE5555E5Z9',
    taxableVal: 200000,
    cgst: 0,
    sgst: 0,
    igst: 36000,
    totalGst: 36000,
    totalInv: 236000,
    itcStatus: 'Eligible',
    type: 'B2B'
  },
  {
    id: 6,
    invoiceDate: '2024-06-18',
    invoiceNo: 'PURCH/24-25/006',
    supplierName: 'Furniture House',
    gstin: '19FFFFF6666F6Z0',
    taxableVal: 30000,
    cgst: 2700,
    sgst: 2700,
    igst: 0,
    totalGst: 5400,
    totalInv: 35400,
    itcStatus: 'Ineligible',
    type: 'B2C'
  }
];

const MONTHS = [
  { v: '', label: 'All Months' },
  { v: '04', label: 'April' },
  { v: '05', label: 'May' },
  { v: '06', label: 'June' },
  { v: '07', label: 'July' },
  { v: '08', label: 'August' },
  { v: '09', label: 'September' },
  { v: '10', label: 'October' },
  { v: '11', label: 'November' },
  { v: '12', label: 'December' },
  { v: '01', label: 'January' },
  { v: '02', label: 'February' },
  { v: '03', label: 'March' }
];
const FY_LIST = ['2024-25', '2023-24', '2022-23'];
const TYPE_OPTIONS = ['B2B', 'B2C'];
const ITC_OPTIONS = ['Eligible', 'Ineligible'];

const itcColor = (s) => ({ Eligible: 'success', Ineligible: 'error' }[s] || 'default');

// ── KPI cards ─────────────────────────────────────────────────────────
const getKPIs = (rows) => ({
  totalPurchase: rows.reduce((s, r) => s + r.totalInv, 0),
  totalGst: rows.reduce((s, r) => s + r.totalGst, 0),
  itcEligible: rows.filter((r) => r.itcStatus === 'Eligible').reduce((s, r) => s + r.totalGst, 0),
  invoiceCount: rows.length
});

export default function GSTPurchase() {
  const [filterName, setFilterName] = useState('');
  const [isOpenFilter, setOpenFilter] = useState(false);
  const [filterType, setFilterType] = useState(' ');
  const [filterITC, setFilterITC] = useState(' ');
  const [filterMonth, setFilterMonth] = useState('');
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('invoiceDate');
  const [exportAnchor, setExportAnchor] = useState(null);

  // download dialog
  const [dlOpen, setDlOpen] = useState(false);
  const [dlYear, setDlYear] = useState('2024-25');
  const [dlMonth, setDlMonth] = useState('');

  const handleRequestSort = (_, property) => {
    setOrder(orderBy === property && order === 'asc' ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const filtered = useMemo(() => {
    let data = [...MOCK_PURCHASES];
    if (filterName)
      data = data.filter(
        (r) =>
          r.invoiceNo.toLowerCase().includes(filterName.toLowerCase()) ||
          r.supplierName.toLowerCase().includes(filterName.toLowerCase())
      );
    if (filterType !== ' ') data = data.filter((r) => r.type === filterType);
    if (filterITC !== ' ') data = data.filter((r) => r.itcStatus === filterITC);
    if (filterMonth) data = data.filter((r) => r.invoiceDate.slice(5, 7) === filterMonth);
    return lodashOrderBy(data, [orderBy], [order]);
  }, [filterName, filterType, filterITC, filterMonth, order, orderBy]);

  const kpis = getKPIs(MOCK_PURCHASES);

  const handleExport = (type) => {
    setExportAnchor(null);
    console.log('GST Purchase export as', type);
  };

  const handleDownload = () => {
    const monthLabel = MONTHS.find((m) => m.v === dlMonth)?.label || 'All Months';
    const rows = dlMonth
      ? MOCK_PURCHASES.filter((r) => r.invoiceDate.slice(5, 7) === dlMonth)
      : MOCK_PURCHASES;
    const tableRows = rows
      .map(
        (r) => `
            <tr>
                <td>${r.invoiceDate}</td>
                <td><strong>${r.invoiceNo}</strong></td>
                <td>${r.supplierName}</td>
                <td style="font-size:11px">${r.gstin}</td>
                <td>₹ ${(r.taxableVal || 0).toLocaleString('en-IN')}</td>
                <td>₹ ${(r.totalGst || 0).toLocaleString('en-IN')}</td>
                <td><strong>₹ ${(r.totalInv || 0).toLocaleString('en-IN')}</strong></td>
                <td style="color:${r.itcStatus === 'Eligible' ? '#16a34a' : '#dc2626'}">${
          r.itcStatus
        }</td>
            </tr>`
      )
      .join('');
    const totalInv = rows.reduce((s, r) => s + (r.totalInv || 0), 0);
    const totalGst = rows.reduce((s, r) => s + (r.totalGst || 0), 0);
    const html = `<!DOCTYPE html><html><head><title>GST Purchases – FY ${dlYear} – ${monthLabel}</title>
        <style>
            body { font-family: Arial, sans-serif; padding: 32px; color: #1e293b; }
            h2  { color: #0d4689; margin-bottom: 4px; }
            p   { color: #64748b; font-size: 13px; margin-top: 0; margin-bottom: 20px; }
            table { width: 100%; border-collapse: collapse; font-size: 12px; }
            th  { background: #0d4689; color: #fff; padding: 8px 10px; text-align: left; }
            td  { padding: 7px 10px; border-bottom: 1px solid #e2e8f0; }
            tr:nth-child(even) td { background: #f8fafc; }
            tfoot td { background: #eff6ff; font-weight: bold; border-top: 2px solid #0d4689; }
            @media print { body { padding: 16px; } }
        </style></head><body>
        <h2>GST Purchase Report</h2>
        <p>Financial Year: <strong>FY ${dlYear}</strong> &nbsp;|&nbsp; Month: <strong>${monthLabel}</strong> &nbsp;|&nbsp; Generated: <strong>${new Date().toLocaleDateString(
      'en-IN'
    )}</strong> &nbsp;|&nbsp; Total Records: <strong>${rows.length}</strong></p>
        <table>
            <thead><tr><th>Date</th><th>Invoice No.</th><th>Supplier</th><th>GSTIN</th><th>Taxable Value</th><th>Total GST</th><th>Total Invoice</th><th>ITC Status</th></tr></thead>
            <tbody>${tableRows}</tbody>
            <tfoot><tr><td colspan="4">Grand Total</td><td></td><td>₹ ${totalGst.toLocaleString(
              'en-IN'
            )}</td><td>₹ ${totalInv.toLocaleString('en-IN')}</td><td></td></tr></tfoot>
        </table>
        <script>window.onload = () => { window.print(); }<\/script>
        </body></html>`;
    const win = window.open('', '_blank');
    win.document.write(html);
    win.document.close();
    setDlOpen(false);
  };

  return (
    <Box>
      {/* ── 4 KPI Cards ── */}
      <Grid container spacing={2} mb={3}>
        {[
          {
            label: 'Total Purchases',
            value: kpis.totalPurchase,
            icon: CallReceivedIcon,
            iconBg: '#eff6ff',
            iconColor: '#1d4ed8',
            border: '#1d4ed8',
            isMoney: true
          },
          {
            label: 'Total GST Paid',
            value: kpis.totalGst,
            icon: ReceiptIcon,
            iconBg: '#fff7ed',
            iconColor: '#ea580c',
            border: '#ea580c',
            isMoney: true
          },
          {
            label: 'ITC Eligible',
            value: kpis.itcEligible,
            icon: CheckCircleOutlineIcon,
            iconBg: '#f0fdf4',
            iconColor: '#16a34a',
            border: '#16a34a',
            isMoney: true
          },
          {
            label: 'Total Invoices',
            value: kpis.invoiceCount,
            icon: AccountBalanceWalletIcon,
            iconBg: '#f0f9ff',
            iconColor: '#0891b2',
            border: '#4fbbc8',
            isMoney: false
          }
        ].map((kpi) => {
          const IconComp = kpi.icon;
          return (
            <Grid item xs={12} sm={6} md={3} key={kpi.label}>
              <Card
                sx={{
                  p: 2.5,
                  borderLeft: `4px solid ${kpi.border}`,
                  borderRadius: 2,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2
                }}
              >
                <Box
                  sx={{
                    p: 1.2,
                    bgcolor: kpi.iconBg,
                    borderRadius: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}
                >
                  <IconComp sx={{ color: kpi.iconColor, fontSize: '1.4rem' }} />
                </Box>
                <Box>
                  <Typography
                    variant="caption"
                    sx={{ fontSize: 11, color: '#718096', fontWeight: 500, display: 'block' }}
                  >
                    {kpi.label}
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 800, fontSize: 17, mt: 0.2, color: '#1e293b' }}
                  >
                    {kpi.isMoney ? `₹ ${kpi.value.toLocaleString('en-IN')}` : kpi.value}
                  </Typography>
                </Box>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* ── Table Card ── */}
      <Card>
        {/* ── Collapsible filters ── */}
        {isOpenFilter && (
          <Box px={3} pt={2} pb={2} sx={{ borderBottom: '1px solid #eaedf1', bgcolor: '#fafbfc' }}>
            <Grid container spacing={2} alignItems="flex-end">
              {/* Month */}
              <Grid item xs={12} sm={3}>
                <Typography className="formLabel" sx={{ mb: 0.5 }}>
                  Month
                </Typography>
                <FormControl fullWidth size="small">
                  <Select
                    displayEmpty
                    value={filterMonth}
                    onChange={(e) => setFilterMonth(e.target.value)}
                    sx={{ fontSize: 13 }}
                  >
                    {MONTHS.map((m) => (
                      <MenuItem key={m.v} value={m.v} sx={{ fontSize: 13 }}>
                        {m.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              {/* Invoice Type */}
              <Grid item xs={12} sm={3}>
                <Typography className="formLabel" sx={{ mb: 0.5 }}>
                  Invoice Type
                </Typography>
                <FormControl fullWidth size="small">
                  <Select
                    displayEmpty
                    value={filterType}
                    onChange={(e) => setFilterType(e.target.value)}
                    sx={{ fontSize: 13 }}
                  >
                    <MenuItem value=" ">All Types</MenuItem>
                    {TYPE_OPTIONS.map((o) => (
                      <MenuItem key={o} value={o} sx={{ fontSize: 13 }}>
                        {o}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              {/* ITC Status */}
              <Grid item xs={12} sm={3}>
                <Typography className="formLabel" sx={{ mb: 0.5 }}>
                  ITC Status
                </Typography>
                <FormControl fullWidth size="small">
                  <Select
                    displayEmpty
                    value={filterITC}
                    onChange={(e) => setFilterITC(e.target.value)}
                    sx={{ fontSize: 13 }}
                  >
                    <MenuItem value=" ">All Status</MenuItem>
                    {ITC_OPTIONS.map((o) => (
                      <MenuItem key={o} value={o} sx={{ fontSize: 13 }}>
                        {o}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              {/* Reset */}
              <Grid item xs="auto">
                <Tooltip title="Reset Filters">
                  <IconButton
                    size="small"
                    className="squareIconButton"
                    onClick={() => {
                      setFilterType(' ');
                      setFilterITC(' ');
                      setFilterMonth('');
                      setFilterName('');
                    }}
                  >
                    <FilterListOffIcon sx={{ fontSize: '1rem' }} />
                  </IconButton>
                </Tooltip>
              </Grid>
            </Grid>
          </Box>
        )}

        {/* ── Toolbar ── */}
        <RootStyle sx={{ minHeight: 45, borderBottom: '1px solid #eaedf1' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {/* <Tooltip title="Export data" placement="top" arrow>
                            <IconButton className="squareIconButton" onClick={(e) => setExportAnchor(e.currentTarget)}>
                                <MdOutlineSystemUpdateAlt size={18} />
                            </IconButton>
                        </Tooltip> */}
            <Menu
              anchorEl={exportAnchor}
              open={Boolean(exportAnchor)}
              onClose={() => setExportAnchor(null)}
              PaperProps={{
                sx: { borderRadius: 1, minWidth: 160, boxShadow: '0 4px 16px rgba(0,0,0,0.1)' }
              }}
            >
              <MenuItem onClick={() => handleExport('pdf')} sx={{ fontSize: 13 }}>
                <PictureAsPdfIcon sx={{ fontSize: 16, mr: 1, color: '#dc2626' }} /> Export as PDF
              </MenuItem>
              <MenuItem onClick={() => handleExport('excel')} sx={{ fontSize: 13 }}>
                <GridOnIcon sx={{ fontSize: 16, mr: 1, color: '#16a34a' }} /> Export as Excel
              </MenuItem>
            </Menu>

            {/* Download monthly data */}
            <Tooltip title="Download Monthly Data" placement="top" arrow>
              <IconButton className="squareIconButton" onClick={() => setDlOpen(true)}>
                <DownloadIcon sx={{ fontSize: '1.1rem' }} />
              </IconButton>
            </Tooltip>
          </Box>

          <Stack direction="row" alignItems="center" spacing={1}>
            <SearchStyle
              value={filterName}
              onChange={(e) => setFilterName(e.target.value)}
              placeholder="Search invoice, supplier…"
              startAdornment={
                <InputAdornment position="start">
                  <Box component={Icon} icon={searchFill} sx={{ color: 'text.disabled' }} />
                </InputAdornment>
              }
            />
            <Tooltip title="Filters">
              <IconButton className="squareIconButton" onClick={() => setOpenFilter(!isOpenFilter)}>
                <Icon icon={roundFilterList} />
              </IconButton>
            </Tooltip>
          </Stack>
        </RootStyle>

        {/* ── Table ── */}
        <Scrollbar>
          <TableContainer sx={{ minWidth: 900, height: '50vh', overflow: 'auto' }}>
            <Table stickyHeader size="small">
              <UserListHead
                order={order}
                orderBy={orderBy}
                headLabel={TABLE_HEAD}
                rowCount={filtered.length}
                onRequestSort={handleRequestSort}
              />
              <TableBody>
                {filtered.map((row) => (
                  <StyledTableRow key={row.id}>
                    <StyledTableCell>{row.invoiceDate}</StyledTableCell>
                    <StyledTableCell sx={{ fontWeight: 600, color: '#0d4689' }}>
                      {row.invoiceNo}
                    </StyledTableCell>
                    <StyledTableCell>{row.supplierName}</StyledTableCell>
                    <StyledTableCell sx={{ fontSize: 10 }}>{row.gstin}</StyledTableCell>
                    <StyledTableCell>₹ {row.taxableVal.toLocaleString()}</StyledTableCell>
                    <StyledTableCell>
                      {row.cgst ? `₹ ${row.cgst.toLocaleString()}` : '–'}
                    </StyledTableCell>
                    <StyledTableCell>
                      {row.sgst ? `₹ ${row.sgst.toLocaleString()}` : '–'}
                    </StyledTableCell>
                    <StyledTableCell>
                      {row.igst ? `₹ ${row.igst.toLocaleString()}` : '–'}
                    </StyledTableCell>
                    <StyledTableCell sx={{ fontWeight: 700 }}>
                      ₹ {row.totalGst.toLocaleString()}
                    </StyledTableCell>
                    <StyledTableCell sx={{ fontWeight: 700 }}>
                      ₹ {row.totalInv.toLocaleString()}
                    </StyledTableCell>
                    <StyledTableCell>
                      <Label variant="ghost" color={itcColor(row.itcStatus)}>
                        {row.itcStatus}
                      </Label>
                    </StyledTableCell>
                  </StyledTableRow>
                ))}
              </TableBody>
              {filtered.length === 0 && (
                <TableBody>
                  <TableRow>
                    <TableCell align="center" colSpan={11} sx={{ py: 4 }}>
                      <SearchNotFound searchQuery={filterName} />
                    </TableCell>
                  </TableRow>
                </TableBody>
              )}
            </Table>
          </TableContainer>
        </Scrollbar>
        <SimplePagination page={page} setPage={setPage} hasNextPage={false} />
      </Card>

      {/* DOWNLOAD MONTHLY DATA DIALOG */}
      <Dialog open={dlOpen} onClose={() => setDlOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle
          sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}
        >
          <Typography sx={{ fontWeight: 700, fontSize: 15, color: '#0d4689' }}>
            Download GST Purchases
          </Typography>
          <IconButton size="small" onClick={() => setDlOpen(false)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent>
          <Box pt={1}>
            <Typography variant="body2" sx={{ color: '#64748b', mb: 2.5, fontSize: 13 }}>
              Select Financial Year and Month to generate and download a PDF report.
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography className="formLabel" sx={{ mb: 0.5 }}>
                  Financial Year
                </Typography>
                <FormControl fullWidth size="small">
                  <Select
                    value={dlYear}
                    onChange={(e) => setDlYear(e.target.value)}
                    sx={{ fontSize: 13 }}
                  >
                    {FY_LIST.map((fy) => (
                      <MenuItem key={fy} value={fy} sx={{ fontSize: 13 }}>
                        FY {fy}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <Typography className="formLabel" sx={{ mb: 0.5 }}>
                  Month
                </Typography>
                <FormControl fullWidth size="small">
                  <Select
                    displayEmpty
                    value={dlMonth}
                    onChange={(e) => setDlMonth(e.target.value)}
                    sx={{ fontSize: 13 }}
                  >
                    {MONTHS.map((m) => (
                      <MenuItem key={m.v} value={m.v} sx={{ fontSize: 13 }}>
                        {m.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 3, py: 1.5, gap: 1 }}>
          <Button onClick={() => setDlOpen(false)} sx={{ textTransform: 'none', color: '#64748b' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            className="mainBtn"
            startIcon={<DownloadIcon />}
            onClick={handleDownload}
            sx={{ textTransform: 'none' }}
          >
            Download
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
